import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import {
  Button,
  ButtonContainer,
  Card,
  FancyButtonLink,
  Loader,
  Message,
} from "shared/components";
import { Spacer } from "shared/layout";
import { CenterContent } from "shared/styles";
import { ContentWidth } from "shared/styles/constants";
import { IAPIState, IJSONAPIErrorCodes, IStatus, ITheme } from "shared/types";

import { useProfileContext } from "Auth/contexts";
import { BAND_PATHS } from "Bands/router";
import {
  acceptBandInvitation,
  dataMetaSelector,
  dispatchGetUserBands,
} from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import {
  useAPIError,
  useBandRoute,
  useClearActionMetaOnUnMount,
  useGetBandInvitation,
} from "Bands/utils";
import { useHistory } from "react-router-dom";
import { buildUrl } from "shared/utils";

const acceptInvitationCopy = copy.members.acceptInvitation;

export const AcceptInvitation: React.FC = () => {
  const { bandInvitationId, bandInvitation, bandInvitationMeta } =
    useGetBandInvitation();

  const { pushRoute } = useBandRoute();
  const dispatch = useDispatch();
  const { email: currentEmail, handleLogout } = useProfileContext();
  const [initiallyTried, setInitiallyTried] = useState(false);

  const bandId = useMemo(() => bandInvitation?.band.id, [bandInvitation]);

  const { api: acceptMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.acceptBandInvitation)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.acceptBandInvitation);

  const inviteCurrentUser = useMemo(
    () => currentEmail === bandInvitation?.email,
    [bandInvitation, currentEmail]
  );

  const handleAcceptInvitation = useCallback(() => {
    if (!bandInvitation || initiallyTried) return;

    setInitiallyTried(true);

    setTimeout(() => {
      dispatch(acceptBandInvitation(bandInvitation.id));
    }, 200);
  }, [bandInvitation, initiallyTried, dispatch]);

  useEffect(() => {
    // automatically accept invitation if the invite
    // was sent to current user
    if (inviteCurrentUser && !initiallyTried) {
      handleAcceptInvitation();
    }
  }, [inviteCurrentUser, handleAcceptInvitation, initiallyTried]);

  useEffect(() => {
    if (!bandId) return;

    if (acceptMeta?.status === IAPIState.succeeded) {
      // refetch user bands
      dispatchGetUserBands(dispatch);
    }
  }, [acceptMeta?.status, dispatch, bandId]);

  const isSuccess = useMemo(
    () =>
      bandInvitationMeta?.status === IAPIState.succeeded &&
      acceptMeta?.status === IAPIState.succeeded,
    [bandInvitationMeta?.status, acceptMeta?.status]
  );
  const isPending = useMemo(
    () =>
      bandInvitationMeta?.status === IAPIState.pending ||
      acceptMeta?.status === IAPIState.pending,
    [bandInvitationMeta?.status, acceptMeta?.status]
  );
  const hasFailed = useMemo(
    () =>
      bandInvitationMeta?.status === IAPIState.failed ||
      acceptMeta?.status === IAPIState.failed,
    [bandInvitationMeta?.status, acceptMeta?.status]
  );

  const { error: alreadyMemberError } = useAPIError({
    apiError: acceptMeta?.error,
    message: acceptInvitationCopy.messages.alreadyMember,
    errorCode: IJSONAPIErrorCodes.NotUniqueError,
  });

  const history = useHistory();
  const goToCalendar = useCallback(() => {
    history.push(buildUrl(BAND_PATHS.CALENDAR, {}));
  }, [history]);

  const renderSuccess = useCallback(
    () => (
      <Spacer size="m">
        <Message
          status={IStatus.success}
          message={acceptInvitationCopy.messages.success
            .replace("%band", bandInvitation?.band.title!)
            .toString()}
        />
        {bandId && (
          <Button
            onClick={() => pushRoute(BAND_PATHS.BAND_CALENDAR, { bandId })}
          >
            {acceptInvitationCopy.buttons.goToBandCalendar}
          </Button>
        )}
      </Spacer>
    ),
    [bandId, bandInvitation?.band.title, pushRoute]
  );

  const renderError = useCallback(() => {
    let message: string = acceptInvitationCopy.messages.failed;

    if (typeof alreadyMemberError === "string") {
      message = alreadyMemberError;
    }

    return (
      <Spacer size="m">
        <Message status={IStatus.error} message={message} />
        <Button onClick={goToCalendar}>
          {acceptInvitationCopy.buttons.goToCalendar}
        </Button>
      </Spacer>
    );
  }, [alreadyMemberError, goToCalendar]);

  const renderContent = useCallback(() => {
    if (isSuccess) return renderSuccess();
    if (hasFailed) return renderError();

    if (inviteCurrentUser) {
      if (bandInvitationId && isPending) return <Loader />;

      return null;
    }

    if (!bandInvitation) {
      if (bandInvitationMeta?.status === IAPIState.pending) {
        return <Loader />;
      }

      return renderError();
    }

    return (
      <Spacer size="s">
        <Message status={IStatus.warning}>
          {acceptInvitationCopy.messages.invitedAs
            .replace("%invite", bandInvitation.email)
            .replace("%email", currentEmail)}
        </Message>
        <ButtonContainer>
          <Button onClick={handleAcceptInvitation}>
            <span>{acceptInvitationCopy.buttons.accept}</span>
            {isPending && <Loader size="s" />}
          </Button>
          <Button onClick={goToCalendar} theme={ITheme.secondary}>
            {acceptInvitationCopy.buttons.cancel}
          </Button>
          <FancyButtonLink onClick={handleLogout} status={IStatus.error}>
            {acceptInvitationCopy.buttons.logout}
          </FancyButtonLink>
        </ButtonContainer>
      </Spacer>
    );
  }, [
    bandInvitation,
    bandInvitationId,
    bandInvitationMeta?.status,
    currentEmail,
    goToCalendar,
    handleAcceptInvitation,
    handleLogout,
    hasFailed,
    inviteCurrentUser,
    isPending,
    isSuccess,
    renderError,
    renderSuccess,
  ]);

  return (
    <CenterContent width={ContentWidth.default}>
      <Card>{renderContent()}</Card>
    </CenterContent>
  );
};
